import type { IAppBannerImage } from '@/configs/interfaces/IAppBannerImage';
import { TMemberClass } from '@/models/Auth/interfaces/IAuthValidatePayload';
import { IMemberAgreeAgreements } from '@/models/Auth/interfaces/IAuthValidateSerialized';
import CartActions from '@/models/Cart/CartActions';
import CartRequestBuilder from '@/models/Cart/CartRequestBuilder';
import { EN_CONFIRM_PASSWORD_SESSION_KEY } from '@/models/Member/interfaces/IMemberConfirmPasswordRequestParams';
import { TJoyJoinType } from '@/models/Member/interfaces/TMember';
import { PlaceDateFilterChangedLocation } from '@/models/Session/interfaces/ISessionSetPlaceFiltersRequestParams';
import { IHTTPParams, IRequestWrap, IResponseWrap } from '@/server/IServer';
import { Criteo, GA4 } from '@/services/TrackerService';
import { captureSentryOnUnsupportedPage, getSentryRelease } from '@/services/TrackerService/Sentry';
import { YaMo } from '@/services/YaMoment';
import GlobalStore, { COOKIE_SMART_BANNER, GlobalStoreCtx, ISmartBanner } from '@/stores/GlobalStore';
import { IS_PRODUCTION_ENV, setWebViewEntryURL } from '@/utils';
import { notNeedToCartApi } from '@/utils/ApiUtils';
import { EN_APP_DEVICE, getAppDevice, getDevice } from '@/utils/functions/device';
import { sendImpressionLog } from '@/utils/hooks/useImpressionTracker';
import { DEFAULT_PAGE_SWIPE_BACK, usePageSwipeBack } from '@/utils/hooks/useSwipeBack';
import * as SentryBrowser from '@sentry/browser';
import * as Sentry from '@sentry/node';
import { Integrations } from '@sentry/tracing';
import { getYaNative } from '@yanolja-org/ya-native';
import debug from 'debug';
import { NextComponentType } from 'next';
import { path, prop, tryCatch } from 'ramda';
import React, { useEffect, useMemo } from 'react';

import CartNotAvailablePopup from '../Cart/NotAvailable/CartNotAvailablePopup';
import SunnyHead, { ISunnyHeadProps } from './SunnyHead';
import { SunnyFC } from './SunnyNext';
import Toast from './Toast/Toast';

export interface ISunnyPageProps {
  appDevice: EN_APP_DEVICE | null;
  cid: string; // checkinDate
  cod: string; // checkoutDate
  /** 날짜 토스트 표시 여부 */
  dateToast?: boolean;
  device: EN_APP_DEVICE | null;
  isLoggedIn: boolean;
  hashedMemberNo: string;
  referrer: string;
  requestedPage?: string;
  requestedUrl: string;
  sentryDSN: string;
  statusCode: number;
  cartCount: number;
  cartLink: string;
  hasSmartBannerCookie: boolean;
  trackcode?: string;
  isNaverHotelEvent: boolean;
  memberType?: TJoyJoinType;
  isKakaoChannel: boolean;
  isKakaoEmbedded: boolean;
  appBannerImage: IAppBannerImage;
  browserName?: string;
  agreements?: IMemberAgreeAgreements;
  memberClass?: TMemberClass;
}

/**
 * @param R 리퀘스트 리소스
 * @param P 리퀘스트 파라미터
 * @param Q 엔드포인트 쿼리
 */
export interface IGetInitialPropsParams<P extends IHTTPParams = any, Q = any> {
  pathname: string;
  query: Q;
  asPath: string;
  req: IRequestWrap<P>;
  res: IResponseWrap;
  err: any;
}

export interface IDateToastDecorator {
  /**
   * 날짜 변경 토스트 표시 여부를 반환하는 함수.
   * @param dateChangedAt 날짜를 변경했던 페이지
   * @returns `true`면 표시, `false`면 연기
   */
  resolver: (dateChangedAt?: PlaceDateFilterChangedLocation) => boolean;
}

export interface ISmartBannerDecorator {
  (props: any): ISmartBanner | null;
}

interface IWithSunnyPageParams {
  dateToast?: true | IDateToastDecorator;
  smartBanner?: ISmartBannerDecorator;
  /**
   * 오류 처리를 페이지에서 할 수 있으면 `true`. 즉,
   *
   * ```ts
   * // SunnyPage
   * if (!shouldRenderError && props.statusCode && props.statusCode >= 400) {
   *   return null;
   * }
   * ```
   */
  shouldRenderError?: boolean;
}

const perfLog = debug('perf:SunnyPage');

/**
 * 뒤로가기 버튼/제스쳐로 인한 클라이언트 사이드 렌더링을 방지하고, 세션 조작 및 필수 프롭을 설정하며,
 * 전역 스토어 등 렌더링 시 필요한 공통 항목도 초기화해 제공하는 HoC를 반환한다.
 *
 * 반환한 HoC는 매개변수로 `SunnyFC` 형식만 받을 수 있다.
 *
 * @example
 * declare const Foo: SunnyFC<IFooProps>;
 *
 * export default withSunnyPage({ ...IWithSSROnlyDecorator })(Foo);
 */
export const withSunnyPage = ({ dateToast, smartBanner, shouldRenderError }: IWithSunnyPageParams = {}) => (
  PageComponent: SunnyFC<any, any>,
): unknown => {
  const wrapped: NextComponentType<
    IGetInitialPropsParams,
    ISunnyPageProps | void,
    ISunnyPageProps & { headProps: ISunnyHeadProps }
  > = (props) => {
    const globalStore = useMemo(
      () =>
        new GlobalStore({
          appDevice: props.appDevice,
          device: props.device,
          referrer: props.referrer,
          cartCount: props.cartCount,
          cartLink: props.cartLink,
          hasSmartBannerCookie: props.hasSmartBannerCookie,
          trackcode: props.trackcode,
          isKakaoChannel: props.isKakaoChannel,
          isKakaoEmbedded: props.isKakaoEmbedded,
          isNaverHotelEvent: props.isNaverHotelEvent,
          browserName: props.browserName,
          agreements: props.agreements,
          memberType: props.memberType,
          memberClass: props.memberClass,
          isLoggedIn: props.isLoggedIn,
          requestedUrl: props.requestedUrl,
        }),
      [],
    );
    if (typeof smartBanner === 'function' && !props.appDevice) {
      globalStore.SmartBanner = { ...smartBanner(props), image: props.appBannerImage };
    }

    // IOS 스와이프백 설정
    usePageSwipeBack(DEFAULT_PAGE_SWIPE_BACK, true);

    useEffect(() => {
      window.ya__.isLoggedIn = props.isLoggedIn;
      window.ya__.memberClass = props.memberClass;
      window.ya__.hashedMemberNo = props.hashedMemberNo;

      GA4.initialize({ gaApiKey: window.ya__.ga4ID });
      Criteo.initialize();

      // [todo] userId: cgntID
      const sentryOrigins = /https:\/\/((dev|qa|stage)-m|www|assets)\.yanolja\.com/;
      SentryBrowser.init({
        enabled: IS_PRODUCTION_ENV,
        dsn: props.sentryDSN,
        environment: window.ya__.runMode,
        release: getSentryRelease(window.ya__.runMode, window.ya__.version),
        allowUrls: [sentryOrigins],
        integrations: [
          new Integrations.BrowserTracing({
            tracingOrigins: [sentryOrigins],
          }),
        ],
        tracesSampler: (samplingContext) => {
          const op = samplingContext.transactionContext.op;
          if (op === 'pageload' || op === 'navigation') {
            return 0.05;
          }
          return 0;
        },
      });

      // accio.initialize({
      //   identityPoolID: window.ya__.identityPoolID,
      //   isLoggedIn: props.isLoggedIn,
      //   streamName: window.ya__.streamName,
      //   version: window.ya__.version,
      // });

      setWebViewEntryURL();

      if (props.dateToast) {
        const format = 'M/D (ddd)';
        const checkinDate = YaMo.getSeoulTime(props.cid).format(format);
        const checkoutDate = YaMo.getSeoulTime(props.cod).format(format);

        // '이전' 날짜와 같지 않아야 토스트 추가
        if (
          checkinDate !== sessionStorage.getItem('checkinDate') ||
          checkoutDate !== sessionStorage.getItem('checkoutDate')
        ) {
          globalStore.toast.addItem({
            key: 'date-changed',
            text: (
              <>
                변경한 날짜 {`${checkinDate} - ${checkoutDate}`}
                <br />로 둘러보세요.
              </>
            ),
            timeout: 2500,
          });
        }
      }

      if (props.requestedPage) {
        GA4.pageView({
          page: props.requestedPage,
          title: document.title,
        });
      }

      document.addEventListener('readystatechange', () => {
        if (document.readyState === 'complete' && window.performance?.timing) {
          const perfData = window.performance.timing;
          perfLog(perfData.domComplete - perfData.domLoading);
        }
      });

      /**
       * 네이티브 환경에서 웹뷰 열릴때 스택 구조로 페이지가 쌓이기 때문에 PDP 에서 뒤로가기로 웹뷰로 돌아올때
       * PDP 레이아웃이 dismiss 되어 웹뷰에서 이를 감지하여 count 업데이트 할 수 없음.
       *
       * 이를 해결하기 위해 네이티브 환경일때 visibilitychange 이벤트를 등록하여
       * visible 되었을때 장바구니 카운트를 업데이트함.
       */
      const yaNative = getYaNative();
      const handleVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
          void globalStore.updateCartCount();
        }
      };
      if (yaNative.isEnabled) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }

      // 수집 되어있는 impr로그 전송
      sendImpressionLog();

      /*
       * 네이버호텔로 유입시 label이 붙어 있는경우 label을 주소에서 삭제한다.
       * [FES-1129] 네이버 호텔을 통한 NOL 유입_라벨 개선 요청(개인정보이슈)
       * */
      const trackCodes = globalStore.TrackCode?.split('|');
      if (trackCodes && trackCodes[0] === 'naverhotel') {
        history.replaceState(
          null,
          '',
          `${location.pathname}${location.search
            .split('&')
            .filter((v) => !/label/.test(v))
            .join('&')}`,
        );
      }

      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    if (!shouldRenderError && props.statusCode && props.statusCode >= 400) {
      return null;
    }

    return (
      <GlobalStoreCtx.Provider value={globalStore}>
        <SunnyHead {...props.headProps} />
        <PageComponent {...props} />
        <Toast />
        <CartNotAvailablePopup />
      </GlobalStoreCtx.Provider>
    );
  };
  wrapped.getInitialProps = async (ctx) => {
    // 브라우저에서는 강제로 서버프롭 주입 후 쇼트서킷 (사파리 뒤로가기 방어)
    if (process.browser) {
      return window['__NEXT_DATA__']?.['props']?.['pageProps'];
    }

    // [todo] 아래 것들은 미들웨어로 빼는 것이 좋을 듯
    // appDevice: 앱에서 webview 를 열때 넣어주는 식별 헤더값
    const appDevice = getAppDevice(ctx.req);
    if (appDevice) {
      ctx.res.cookie('app-device', appDevice, {
        maxAge: 1000 * 60 * 60 * 48, // 48시간
        domain: '.yanolja.com',
      });
    }
    const adid = ctx.req.headers['adid'] || ctx.req.cookies['adid'];
    if (adid) {
      ctx.res.cookie('adid', adid, {
        domain: '.yanolja.com',
      });
    }
    const appVersion = ctx.req.headers['version'];
    if (appVersion) {
      ctx.res.cookie('version', appVersion, {
        maxAge: 1000 * 60 * 60 * 24 * 365, // 1년
        domain: '.yanolja.com',
      });
    }

    const isPasswordResetPage = /^\/member\/password\/advice/.test(ctx.req.path);

    // 비밀번호 재설정 대상자
    if (ctx.req.session.isPasswdExpired()) {
      // 타서비스 회원이전 파라미터가 있을 경우 강제 로그아웃 시킴
      if (ctx.req.query.referral && ctx.req.query.key && ctx.req.path === '/login') {
        ctx.req.session.clearSession();
      } else if (!isPasswordResetPage) {
        const server = ctx.req.config.Server;
        ctx.res.redirect(ctx.req.session.getPasswdChangeRedirectUrl(`${server.href()}${ctx.req.originalUrl}`));
        return {};
      }
    }

    if (ctx.req.session.isVisitPhoneReCertificate) {
      ctx.req.session.clearAnonymousSession();
    }

    if (ctx.req.session.isAnonymousLoggedIn()) {
      // 로그인에서 넘어온 경우 인증 완료 후 redirect 경로로 넘어가야 한다.
      const fromLogin = ctx.req
        .header('referer')
        ?.split('/')
        .some((path) => path.startsWith('login'));
      const redirectFromReferer =
        (fromLogin &&
          tryCatch(
            (value) => new URL(value).searchParams.get('redirect'),
            () => '',
          )(ctx.req.header('referer') || '')) ||
        '';
      const redirect = redirectFromReferer || ctx.req.header('referer') || '/mypage';

      const phoneNumYN = path<string>(['session', 'phoneNumYN'], ctx.req);

      if (phoneNumYN !== 'Y' && ctx.req.path !== '/phone-re-certificate') {
        // 타서비스 회원이전 파라미터가 있을 경우 강제 로그아웃 시킴
        if (ctx.req.query.referral && ctx.req.query.key) {
          ctx.req.session.clearSession();
        } else if (!isPasswordResetPage) {
          // password 가 리셋되는 경우 (6개월 만료 등의 이유로)에는 점유 인증 페이지로 이동하지 않음
          // [DEFECT-6358], [DEFECT-8880]
          ctx.res.redirect(`/phone-re-certificate?redirect=${encodeURIComponent(redirect)}`);
          return {};
        }
      }
    }

    if (!ctx.req.requestedUrl.includes('/member')) {
      ctx.req.session.clearPasswdConfirmKey(EN_CONFIRM_PASSWORD_SESSION_KEY.MEMBER_INFO);
      ctx.req.session.clearPasswdConfirmKey(EN_CONFIRM_PASSWORD_SESSION_KEY.WITHDRAW);
      ctx.req.session.clearSocialLoginConfirmKey();
    }

    const [checkinDate, checkoutDate] = ctx.req.session.getCheckIODates();

    const needsDateToast =
      ctx.req.session.NeedsDateToast && (dateToast === true || dateToast?.resolver(ctx.req.session.dateChangedAt));

    if (needsDateToast) {
      ctx.req.session.needsDateToast = false;
    }

    // user-agent 에서 파싱된 device 정보
    const device = getDevice(ctx.req);

    // 장바구니 수량
    let cartCount = 0;
    // path를 가지고 장바구니 API호출이 필요한지 판단한다.
    const needCartApi = !notNeedToCartApi(ctx.req.path);
    if (needCartApi) {
      const cartActions = new CartActions(CartRequestBuilder, ctx.req);
      const cartReqParams = {
        headers: {
          cognitoId: ctx.req.cookies['cgntId'],
          ywt: ctx.req.session.isLoggedIn() ? ctx.req.session.getYWT() : undefined,
        },
      };
      const cartCountEntity = await cartActions.cartCount(cartReqParams);
      cartCount = cartCountEntity.map(prop('data')).caseOf({
        left: () => 0,
        right: (r) => {
          return r.data?.count ?? 0;
        },
      });
    }

    const userAgent: string[] = ((ctx.req.headers['user-agent'] as string) || '').split(' ');
    const isKakaoEmbedded = userAgent.some((agent) => agent.includes('KAKAOTALK'));
    const isKakaoChannel = isKakaoEmbedded && userAgent.some((agent) => agent.includes('ch-home'));

    if (isKakaoChannel) {
      ctx.res.cookie(COOKIE_SMART_BANNER, 'Y', {
        maxAge: 1000 * 60 * 60 * 24 * 365, // 1년
        domain: '.yanolja.com',
      });
    }

    // 스마트배너 쿠키, 카카오 채널에서 접근한 경우 앱 설치 배너를 노출하면 안되므로 해당 조건을 같이 체크한다.
    const hasSmartBannerCookie = !!ctx.req.cookies[COOKIE_SMART_BANNER] || (isKakaoChannel && isKakaoEmbedded);
    // 첫 진입시에는 쿠키를 읽을수 없으므로 쿠키에 trackcode 가 존재하지 않으면 query 에서 한번더 확인한다
    const trackcode = ctx.req.cookies['trackcode'] || ctx.req.query['trackcode'];

    // [FES-768] 네이버호텔 프로모션 임시 제거. 추후 재사용될 수 있음.
    // const isNaverHotelEvent = false;
    const isNaverHotelEvent = ctx.req.cookies['trackcode']?.includes('promotion') || ctx.req.query['isnaver'] === '2';

    if (device && appDevice) {
      captureSentryOnUnsupportedPage(ctx.req);
    }

    try {
      const pageCompProps = await PageComponent.getInitialProps?.(ctx);

      return {
        appDevice,
        device,
        cid: checkinDate,
        cod: checkoutDate,
        dateToast: needsDateToast,
        isLoggedIn: ctx.req.session.isLoggedIn(),
        hashedMemberNo: ctx.req.session.getHashedMemberNo(),
        sentryDSN: ctx.req.config.SentryDSN,
        statusCode: ctx.res.statusCode,
        referrer: ctx.req.session.getReferrer(),
        requestedPage: ctx.req.requestedPage,
        requestedUrl: ctx.req.requestedUrl,
        cartCount,
        cartLink: ctx.req.config.PlatformSite.path('/cart').href(),
        ...(pageCompProps || {}),
        hasSmartBannerCookie,
        trackcode,
        isNaverHotelEvent,
        memberType: ctx.req.session.getMemberType(),
        isKakaoChannel,
        isKakaoEmbedded,
        appBannerImage: ctx.req.config.AppBannerImage,
        browserName: ctx.req.ua.getBrowser()?.name,
        agreements: ctx.req.session.getAgreements(),
        memberClass: ctx.req.session.getMemberClass(),
      };
    } catch (error) {
      console.error('Error at SunnyPage', error);
      if (ctx.req.config.env === 'production' && ctx.req.config.mode !== 'local') {
        Sentry.captureException(error);
      }
      ctx.res.status(500);
      return {
        statusCode: 500,
      };
    }
  };

  return wrapped;
};
